import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, X, CheckCircle } from 'lucide-react';
import { api } from '../../api';

export const FeedbackWidget = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState('quick_rating');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) return;
    setLoading(true);
    try {
      await api.request('/feedback', {
        method: 'POST',
        body: JSON.stringify({
          rating,
          feedback_type: feedbackType,
          message: message || null,
          page_context: window.location.pathname
        })
      });
      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setRating(0);
        setMessage('');
      }, 2000);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-[220px] lg:left-[264px] bottom-6 z-30 bg-levelup-accent text-levelup-darker px-2 py-4 rounded-r-lg shadow-lg hover:bg-levelup-accent-hover transition-colors"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        data-testid="feedback-toggle"
      >
        <span className="flex items-center gap-2 font-medium text-sm">
          <Star size={16} />
          {t('feedback.title')}
        </span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-green-500" size={32} />
                </div>
                <h3 className="text-xl font-bold text-levelup-dark">{t('feedback.thankYou')}</h3>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-levelup-dark">{t('feedback.title')}</h3>
                    <p className="text-gray-500 text-sm">{t('feedback.subtitle')}</p>
                  </div>
                  <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('feedback.rateExperience')}</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        className={`p-2 rounded-lg transition-colors ${
                          rating >= star ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'
                        }`}
                      >
                        <Star size={32} fill={rating >= star ? 'currentColor' : 'none'} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('feedback.feedbackType')}</label>
                  <select
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
                  >
                    <option value="quick_rating">{t('feedback.types.quick_rating')}</option>
                    <option value="feature_request">{t('feedback.types.feature_request')}</option>
                    <option value="bug_report">{t('feedback.types.bug_report')}</option>
                    <option value="general">{t('feedback.types.general')}</option>
                  </select>
                </div>

                <div className="mb-6">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t('feedback.messagePlaceholder')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={rating === 0 || loading}
                  className="w-full bg-levelup-accent text-levelup-darker px-6 py-3 rounded-lg font-semibold hover:bg-levelup-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('common.loading') : t('feedback.submit')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
